import { Building2, ExternalLink } from "lucide-react";
import { StatusPill } from "../../../ui";
import { formatEurShort, type IdentityFacts } from "../../../../lib/siteScan";
import { PhoneLink } from "../../../telephony/PhoneLink";
import type { T } from "../types";
import { CopyButton, Field, Panel } from "./primitives";

/**
 * Who owns this site, and how sure we are.
 *
 * The match source is shown on purpose. "The registry code was printed on the
 * page" and "the closest name in the register" are very different claims, and an
 * operator about to address someone by their company name deserves to know which
 * of the two they are looking at.
 */
export function CompanyPanel({ identity, domain, t }: { identity: IdentityFacts; domain?: string; t: T }) {
  const reg = identity.register;

  if (!identity.companyName && !reg) {
    return (
      <Panel icon={Building2} title={t("scanner.company.title")}>
        <p className="text-sm text-muted-foreground">{t("scanner.company.empty")}</p>
      </Panel>
    );
  }

  const uncertain = reg ? reg.confidence < 70 : true;

  return (
    <Panel
      icon={Building2}
      title={t("scanner.company.title")}
      aside={
        identity.source ? (
          // Warning-toned when the match is a guess. "The registry code was
          // printed on the page" and "the closest name in the register" are very
          // different claims, and the pill is where that difference is stated.
          <StatusPill tone={uncertain ? "wt" : "mut"}>
            {t(`scanner.company.source.${identity.source}`)}
          </StatusPill>
        ) : undefined
      }
    >
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-base font-semibold">{identity.companyName ?? "-"}</p>
          {reg && (
            <a
              href={`/ariregister/${reg.regCode}`}
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
            >
              {t("scanner.company.openRegister")} <ExternalLink className="size-3" />
            </a>
          )}
        </div>

        {reg && (
          <dl className="grid grid-cols-2 gap-3 border-t border-[var(--crm-hair)] pt-3 sm:grid-cols-4">
            <Field label={t("scanner.company.regCode")} value={reg.regCode} mono />
            <Field label={t("scanner.company.vat")} value={reg.vatNumber ?? "-"} mono />
            <Field label={t("scanner.company.county")} value={reg.county ?? "-"} />
            <Field label={t("scanner.company.status")} value={reg.status ?? "-"} />
            <Field
              label={t("scanner.company.revenue")}
              value={reg.revenueCents == null ? "-" : `${formatEurShort(reg.revenueCents)} EUR`}
            />
            <Field label={t("scanner.company.employees")} value={reg.employees == null ? "-" : String(reg.employees)} />
            <Field label={t("scanner.company.activity")} value={reg.emtakText ?? "-"} />
            <Field label={t("scanner.company.confidence")} value={`${reg.confidence}%`} />
          </dl>
        )}

        {(identity.email || identity.phone) && (
          <div className="flex flex-wrap items-center gap-3 border-t border-[var(--crm-hair)] pt-3">
            {identity.email && (
              <span className="flex min-w-0 items-center gap-1">
                <a
                  href={`mailto:${identity.email}`}
                  className="min-w-0 truncate text-sm text-primary hover:underline"
                >
                  {identity.email}
                </a>
                <CopyButton value={identity.email} label={t("common.copy")} />
                {identity.emailSource && (
                  <span className="text-[11px] text-muted-foreground">
                    {t(`scanner.company.emailSource.${identity.emailSource}`)}
                  </span>
                )}
              </span>
            )}
            {identity.phone && (
              <span className="flex items-center gap-1">
                <PhoneLink
                  phone={identity.phone}
                  source="scanner"
                  companyName={identity.companyName ?? undefined}
                  regCode={identity.register?.regCode ?? undefined}
                  leadDomain={domain}
                />
                <CopyButton value={identity.phone} label={t("common.copy")} />
              </span>
            )}
          </div>
        )}

        {identity.address && <p className="text-xs text-muted-foreground">{identity.address}</p>}

        {identity.candidates.length > 1 && (
          <div className="border-t border-[var(--crm-hair)] pt-3">
            <p className="mb-1.5 text-[11px] font-medium text-muted-foreground">{t("scanner.company.candidates")}</p>
            <div className="flex flex-wrap gap-1.5">
              {identity.candidates.slice(1, 6).map((c) => (
                <span
                  key={`${c.source}-${c.name}`}
                  title={c.source}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[var(--crm-sunk)] px-2.5 py-1 text-xs"
                >
                  {c.name}
                  <span className="crm-tnum text-[10px] text-muted-foreground">{c.confidence}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </Panel>
  );
}
